import { useEffect, useRef, useState } from 'react';
const LOCAL_STORAGE_UPDATED_EVENT = 'hookdeck:local-storage-updated';
interface LocalStorageUpdatedEventDetail {
  key: string;
  session_only: boolean;
  value: unknown;
}
const useLocalStorage = <T,>(key: string, initial_value: T, session_only = false): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const storage = session_only ? window.sessionStorage : window.localStorage;
  const has_mounted = useRef(false);
  const skip_next_broadcast = useRef(false);
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storage.getItem(key);
      return item !== null && item !== undefined ? JSON.parse(item) : initial_value;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initial_value;
    }
  });

  // This useEffect handles the side effect of writing to localStorage/sessionStorage
  useEffect(() => {
    try {
      if (storedValue === undefined || storedValue === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify(storedValue));
      }
    } catch (error_0) {
      console.warn(`Error setting localStorage key “${key}”:`, error_0);
      return;
    }
    if (!has_mounted.current) {
      has_mounted.current = true;
      return;
    }
    if (skip_next_broadcast.current) {
      skip_next_broadcast.current = false;
      return;
    }
    window.dispatchEvent(new CustomEvent<LocalStorageUpdatedEventDetail>(LOCAL_STORAGE_UPDATED_EVENT, {
      detail: {
        key,
        session_only,
        value: storedValue
      }
    }));
  }, [key, session_only, storedValue, storage]); // Re-run if key, value, or storage type changes

  useEffect(() => {
    const syncStoredValue = (value: T) => {
      setStoredValue(current_value => {
        if (Object.is(current_value, value)) {
          return current_value;
        }
        skip_next_broadcast.current = true;
        return value;
      });
    };
    const handleLocalStorageUpdate = (event: Event) => {
      const {
        detail
      } = event as CustomEvent<LocalStorageUpdatedEventDetail>;
      if (detail.key === key && detail.session_only === session_only) {
        syncStoredValue(detail.value as T);
      }
    };
    const handleStorageUpdate = (event_0: StorageEvent) => {
      if (event_0.key !== key || event_0.storageArea !== storage) {
        return;
      }
      try {
        syncStoredValue(event_0.newValue === null ? initial_value : JSON.parse(event_0.newValue));
      } catch (error_1) {
        console.warn(`Error reading localStorage key “${key}”:`, error_1);
      }
    };
    window.addEventListener(LOCAL_STORAGE_UPDATED_EVENT, handleLocalStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);
    return () => {
      window.removeEventListener(LOCAL_STORAGE_UPDATED_EVENT, handleLocalStorageUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, [initial_value, key, session_only, storage]);
  return [storedValue, setStoredValue];
};
export default useLocalStorage;